import React, { useEffect, useCallback } from 'react';
import { useWalkie } from '../contexts/WalkieContext';
import { WalkieProvider } from '../contexts/WalkieContext';
import PTTButton from './PTTButton';
import ChannelTabs from './ChannelTabs';
import SOSAlertBanner from './SOSAlertBanner';
import TransmissionHistory from './TransmissionHistory';
import ChannelMemberList from './ChannelMemberList';
import ChannelManagementPanel from './ChannelManagementPanel';

function DashboardInner() {
  const {
    connected,
    connecting,
    channels,
    activeChannelId,
    sosAlerts,
    error,
    pttStart,
    pttStop,
    switchChannel,
    isPttLocked,
    isTransmitting,
    logout,
  } = useWalkie();

  // ─── Keyboard shortcuts ───
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't intercept if typing in an input
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

    switch (e.code) {
      case 'Space':
        e.preventDefault();
        if (!e.repeat) pttStart();
        break;
      case 'Escape':
        e.preventDefault();
        // Leave current channel / go back
        break;
      case 'KeyM':
        e.preventDefault();
        // Toggle mute handled in audio engine
        break;
      default:
        // Number keys 1-9: switch channel
        if (e.code >= 'Digit1' && e.code <= 'Digit9') {
          const idx = parseInt(e.code.replace('Digit', '')) - 1;
          if (channels[idx]) {
            switchChannel(channels[idx].id);
          }
        }
        break;
    }
  }, [pttStart, channels, switchChannel]);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.code === 'Space') {
      e.preventDefault();
      pttStop();
    }
  }, [pttStop]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // ─── Connection status ───
  const statusColor = connected ? 'bg-emerald-500' : connecting ? 'bg-yellow-500' : 'bg-red-500';
  const statusText = connected ? '🟢 متصل' : connecting ? '🟡 جاري الاتصال...' : '🔴 غير متصل';

  if (!connected && !connecting) {
    return <LoginScreen />;
  }

  return (
    <div className="flex flex-col h-screen bg-gray-900 text-gray-100" dir="rtl">
      {/* ─── Header ─── */}
      <header className="flex items-center justify-between px-6 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Hemaya" className="h-8" />
          <h1 className="text-xl font-bold text-emerald-400">
            Walkie — غرفة العمليات
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2 text-sm">
            <span className={`w-3 h-3 rounded-full ${statusColor}`}></span>
            {statusText}
          </span>
          <button
            onClick={logout}
            className="text-sm text-gray-400 hover:text-red-400 transition-colors"
          >
            خروج
          </button>
        </div>
      </header>

      {/* ─── Error banner ─── */}
      {error && (
        <div className="px-6 py-2 bg-red-900/50 border-b border-red-700 text-red-300 text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* ─── Channel Management Toolbar ─── */}
      <ChannelManagementPanel />

      {/* ─── SOS Banner ─── */}
      {sosAlerts.length > 0 && (
        <SOSAlertBanner alerts={sosAlerts} />
      )}

      {/* ─── Channel Tabs ─── */}
      <ChannelTabs
        channels={channels}
        activeChannelId={activeChannelId}
        onSwitch={switchChannel}
      />

      {/* ─── Main Content ─── */}
      <div className="flex-1 flex overflow-hidden">
        {/* ── Center: PTT + Status ── */}
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          {isPttLocked && (
            <div className="mb-6 text-yellow-400 text-lg font-semibold">
              ⏳ القناة مشغولة — يرجى الانتظار
            </div>
          )}
          <PTTButton
            isTransmitting={isTransmitting}
            disabled={isPttLocked || !activeChannelId}
            onMouseDown={() => pttStart()}
            onMouseUp={() => pttStop()}
            onMouseLeave={() => isTransmitting && pttStop()}
            onTouchStart={() => pttStart()}
            onTouchEnd={() => pttStop()}
          />
          <p className="mt-4 text-gray-500 text-sm">
            اضغط Space للتحدث • 1-9 لتبديل القنوات • M كتم • Esc خروج
          </p>
        </div>

        {/* ── Right: Members ── */}
        <div className="w-80 border-r border-gray-700 bg-gray-800/50 overflow-hidden">
          <ChannelMemberList />
        </div>
      </div>

      {/* ─── Bottom: Transmission History ─── */}
      <div className="h-48 border-t border-gray-700 bg-gray-800/50">
        <TransmissionHistory />
      </div>
    </div>
  );
}

// ─── Login Screen ───
function LoginScreen() {
  const { login, error } = useWalkie();
  const [employeeId, setEmployeeId] = React.useState('');
  const [password, setPassword] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (employeeId.trim() && password.trim()) login(employeeId.trim(), password.trim());
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900" dir="rtl">
      <div className="w-96 p-8 bg-gray-800 rounded-2xl border border-gray-700">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Hemaya" className="h-20 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-emerald-400 mt-1">غرفة العمليات</h1>
          <p className="text-gray-500 text-sm mt-1">Hemaya Walkie-Talkie</p>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="block text-sm text-gray-400 mb-2">الرقم الوظيفي</label>
          <input
            type="text"
            value={employeeId}
            onChange={e => setEmployeeId(e.target.value)}
            placeholder="Employee ID"
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 focus:border-emerald-500 focus:outline-none"
            autoFocus
          />
          <label className="block text-sm text-gray-400 mb-2 mt-4">كلمة المرور</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 focus:border-emerald-500 focus:outline-none"
          />
          {error && <p className="mt-2 text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            className="w-full mt-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg transition-colors"
          >
            دخول
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Export wrapped with provider ───
export default function OpsRoomDashboard() {
  return (
    <WalkieProvider>
      <DashboardInner />
    </WalkieProvider>
  );
}
